import { supabase } from './supabase';
import { debugLog } from './debugLog';

// =====================================================
// LOG SERVICE - PERSISTENT ERROR TRACKING — ONEFORM
// Fire-and-forget logging to Supabase system_logs table
// =====================================================

type LogLevel = 'error' | 'warn' | 'info';

export interface LogMetadata {
  form_id?: string;
  response_id?: string;
  supabase_code?: string;
  stack?: string;
  error_name?: string;
  payload_size?: number;
  [key: string]: unknown;
}

// Guard de reentrada — evita loop infinito quando logService é chamado
// durante um erro de auth (já que persistLog chama supabase.auth.getUser)
let isLogging = false;

async function persistLog(
  level: LogLevel,
  category: string,
  message: string,
  metadata?: LogMetadata,
): Promise<void> {
  if (isLogging) {
    console.error(`[logService] Reentrant call blocked — [${category}] ${message}`);
    return;
  }

  isLogging = true;
  try {
    // Extrair stack de Error objects no metadata
    const cleanMeta = { ...(metadata ?? {}) };
    if (cleanMeta.error instanceof Error) {
      cleanMeta.stack = cleanMeta.error.stack;
      cleanMeta.error_name = cleanMeta.error.name;
      delete cleanMeta.error;
    }

    // Capturar user_id sem depender de React context
    let userId: string | null = null;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id ?? null;
    } catch {
      // Se getUser falhar, continua com user_id null
    }

    const { error } = await supabase
      .from('system_logs')
      .insert({
        user_id: userId,
        level,
        category,
        message,
        metadata: cleanMeta,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });

    if (error) {
      console.error('[logService] Failed to persist log:', error.message);
    }
  } catch (err) {
    console.error('[logService] Unexpected error:', err);
  } finally {
    isLogging = false;
  }
}

// =====================================================
// EVENT TRACKING — analytics_events table
// Separated from system_logs so product analytics and operational logs
// have independent retention/query patterns. Pipeline shape matches the
// pre-existing analytics_events table (saas-1.19) so no migration needed.
// =====================================================

export interface TrackEventPayload {
  /** Optional form_id for filtering. */
  form_id?: string;
  /** Optional duration since some reference point (ms). */
  duration_ms?: number;
  /** Optional response_count for batch events. */
  response_count?: number;
  /** Library version or platform identifier. */
  lib_version?: string;
  /** false → emits an error_code; default true. */
  success?: boolean;
  /** Error code for failed events. */
  error_code?: string;
  /** Free-form structured metadata. */
  [key: string]: unknown;
}

// Reentry guard for the analytics path (mirrors persistLog).
let isTracking = false;

async function persistTrack(
  eventType: string,
  payload: TrackEventPayload | undefined,
): Promise<void> {
  if (isTracking) {
    console.error(`[logService.track] Reentrant call blocked — [${eventType}]`);
    return;
  }

  isTracking = true;
  try {
    const p = { ...(payload ?? {}) };
    const formId = p.form_id ?? null;
    const durationMs = typeof p.duration_ms === 'number' ? p.duration_ms : null;
    const responseCount = typeof p.response_count === 'number' ? p.response_count : null;
    const libVersion = typeof p.lib_version === 'string' ? p.lib_version : null;
    const success = p.success !== false;
    const errorCode = typeof p.error_code === 'string' ? p.error_code : null;

    // Strip top-level columns out of metadata to avoid duplication.
    delete p.form_id;
    delete p.duration_ms;
    delete p.response_count;
    delete p.lib_version;
    delete p.success;
    delete p.error_code;

    let userId: string | null = null;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id ?? null;
    } catch {
      // If getUser fails, continue with null — anonymous event still useful.
    }

    const { error } = await supabase
      .from('analytics_events')
      .insert({
        event_type: eventType,
        user_id: userId,
        form_id: formId,
        response_count: responseCount,
        duration_ms: durationMs,
        lib_version: libVersion,
        success,
        error_code: errorCode,
        metadata: p,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });

    if (error) {
      console.error('[logService.track] Failed to persist event:', error.message);
    }
  } catch (err) {
    console.error('[logService.track] Unexpected error:', err);
  } finally {
    isTracking = false;
  }
}

export const logService = {
  error(category: string, message: string, metadata?: LogMetadata): void {
    debugLog('error', category, message, metadata);
    persistLog('error', category, message, metadata);
  },

  warn(category: string, message: string, metadata?: LogMetadata): void {
    debugLog('warn', category, message, metadata);
    persistLog('warn', category, message, metadata);
  },

  info(category: string, message: string, metadata?: LogMetadata): void {
    debugLog('info', category, message, metadata);
    persistLog('info', category, message, metadata);
  },

  /**
   * Track a product analytics event. Persists to `analytics_events` table
   * (not `system_logs` — separation of concerns).
   *
   * @param eventType - canonical event name, e.g. `'builder.mobile.mount'`
   * @param payload - optional shape; top-level keys (`form_id`, `duration_ms`,
   *                  `response_count`, `lib_version`, `success`, `error_code`)
   *                  map to dedicated columns. Anything else goes into the
   *                  `metadata` JSONB column.
   */
  track(eventType: string, payload?: TrackEventPayload): void {
    debugLog('info', eventType, 'analytics_event', payload as LogMetadata | undefined);
    void persistTrack(eventType, payload);
  },
};
